import {
  Boutique,
  Buyer,
  Company,
  CompanyType,
  LightCompany,
  Price,
  StoredObject,
} from "@/features/types";
import {
  DenseProformaInvoice,
  ProformaInvoice,
} from "@/features/invoices/proforma-invoices/proforma-invoice.type";
import {
  DenseOrderConfirmationInvoice,
  OrderConfirmationInvoice,
} from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice.type";

export type CreditInvoiceType = "FIXING" | "SEASON" | "CLAIM";
export const CreditInvoiceTypes: CreditInvoiceType[] = ["FIXING", "SEASON"];

export interface CreditInvoice {
  id: number;
  number: string;
  name: string;
  season: string;
  type: CreditInvoiceType;
  issuedToCompany: Pick<Buyer, "id" | "name">;
  issuedByCompany: Company;
  issuedThroughCompany: LightCompany;
  issuedOn: Date;

  amount: Price;

  orderConfirmationInvoices: Pick<OrderConfirmationInvoice, "id">[];
  earningProformaInvoices: Pick<ProformaInvoice, "id">[];
  usingProformaInvoices: Pick<ProformaInvoice, "id">[];
  file: StoredObject | null;
  publicComment: string | null;
  privateComment: string | null;

  isUsed: boolean;

  createdAt: Date;

  isRead: boolean;

  emailThreadMessageId: string | null;

  ownedByCompany?: Pick<Company, "id" | "name" | "type"> | null;
  ownedByCompanyType?: CompanyType | null;
  // 하자 크레딧을 원인 프로포르마의 우노 수수료 계산에서 뺄지. 등록할 때 정한다(기본 안 뺌).
  isUnoFeeDeductible?: boolean;
}

// 에이전시 소유 크레딧은 송금필요금액에서 빼지 않는다 — 에이전시가 따로 정산하는 크레딧이라 바이어가 보낼 금액은 그대로다.
// 송금필요금액 계산과 인보이스 상세별 크레딧 배분에는 이 조건을 통과한 크레딧만 쓴다(소유 정보가 없으면 예전처럼 차감).
export const isDeductibleCredit = (
  credit: Pick<CreditInvoice, "ownedByCompany">
) => credit.ownedByCompany?.type !== "AGENCY";

// 하자 크레딧 중 "수수료 계산 시 차감" 으로 등록된 것만 원인 프로포르마의 우노 수수료 기준에서 뺀다
// (백엔드 _earned_credit_invoices_of 와 같은 조건).
export const isUnoFeeDeductibleCredit = (
  credit: Pick<CreditInvoice, "type" | "isUnoFeeDeductible">
) => credit.type === "FIXING" && !!credit.isUnoFeeDeductible;

// 프로포마 폼이 "사용 크레딧"으로 들고 다니는 최소 정보 — 선택지와 선택된 태그가 같은 라벨을 그릴 수 있어야 한다.
export type UsedCreditInvoiceSummary = Pick<
  CreditInvoice,
  | "id"
  | "season"
  | "name"
  | "amount"
  | "ownedByCompany"
  | "issuedToCompany"
  | "issuedByCompany"
>;

// 프로포르마에서 크레딧을 고를 때의 맥락 — 이 프로포르마의 부티크(원발행사 포함)와 에이전시.
export interface ProformaCreditContext {
  boutiqueIds: (number | undefined)[];
  agencyId?: number;
}

// 타부티크 크레딧 — 바이어 소유인데 발행사가 프로포르마의 부티크(원발행사 포함)도 에이전시도 아닌 크레딧.
// 바이어 몫에서는 빠지지만 부티크에는 전액을 보내야 하므로, 서버가 그 금액을 발행 부티크 앞의
// 에이전시 소유 크레딧으로 만들어 정산한다(백엔드 _cross_boutique_credit_invoices 와 같은 기준).
export const isOtherBoutiqueCredit = (
  credit: Pick<CreditInvoice, "ownedByCompany" | "issuedByCompany">,
  context: ProformaCreditContext
) => {
  if (credit.ownedByCompany?.type === "AGENCY" || !credit.issuedByCompany) {
    return false;
  }
  const allowedIds = [...context.boutiqueIds, context.agencyId].filter(
    (id): id is number => id !== undefined
  );
  return !allowedIds.includes(credit.issuedByCompany.id);
};

// 크레딧 선택지와 선택된 크레딧 태그가 공유하는 한 줄 라벨: 소유 구분 / 발행사 / 바이어 / 시즌 / 이름 / 금액
// 발행사(부티크·에이전시)는 늘 적는다 — 다른 부티크 크레딧까지 보이는 드롭다운에서 어느 부티크 것인지 구분해야 한다.
// 소유는 회사 이름이 아니라 "바이어 소유 / 에이전시 소유"로 적는다 — 컬럼명이 없는 한 줄에서 회사 이름만 있으면
// 바이어 이름과 구분이 안 된다. 에이전시 소유 크레딧은 다른 바이어 앞으로 발행될 수 있어 바이어도 함께 보여준다.
export const formatCreditInvoiceLabel = (
  credit: Omit<UsedCreditInvoiceSummary, "id">,
  {
    t,
    formatAmount,
  }: {
    t: (key: string) => string;
    formatAmount: (amount: CreditInvoice["amount"]) => string;
  }
) =>
  [
    credit.ownedByCompany?.type === "AGENCY"
      ? t("common:owned_by_agency")
      : t("common:owned_by_buyer"),
    // 부티크는 코드(name)만으로는 알아보기 어려워 조직명을 함께 적는다 — 송금 화면과 같은 "[코드] 조직명" 형식.
    credit.issuedByCompany
      ? credit.issuedByCompany.orgName
        ? `[${credit.issuedByCompany.name}] ${credit.issuedByCompany.orgName}`
        : credit.issuedByCompany.name
      : undefined,
    credit.issuedToCompany?.name,
    credit.season,
    credit.name,
    formatAmount(credit.amount),
  ]
    .filter((part) => !!part)
    .join(" / ");

export interface DenseCreditInvoice
  extends Omit<
    CreditInvoice,
    | "orderConfirmationInvoices"
    | "earningProformaInvoices"
    | "usingProformaInvoice"
  > {
  orderConfirmationInvoices: DenseOrderConfirmationInvoice[];
  earningProformaInvoices: DenseProformaInvoice[];
  usingProformaInvoice: DenseProformaInvoice;
}
